import type { Editor } from "@tiptap/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { checkCharsWith, createCharClassifier } from "./charClassifier";
import { chipFor, type PasteChip } from "./pasteFixModel";

/** Long enough that typing a word is one check, not one per letter. */
const DOC_CHECK_DELAY_MS = 800;

/**
 * Paste-to-fix, wired to one editor.
 *
 * - **The underline** follows the document: checked on load and after edits,
 *   so it survives the chip's dismissal and reload without being stored.
 * - **The chip** fires on paste only, never on typing (§6), and says what the
 *   paste did — counted over the pasted text, not the document.
 */
export function usePasteFix(
  editor: Editor | null,
  fetchWithAuth: (url: string, init?: RequestInit) => Promise<Response>,
) {
  const classifier = useMemo(() => createCharClassifier(checkCharsWith(fetchWithAuth)), [fetchWithAuth]);
  const [chip, setChip] = useState<PasteChip | null>(null);
  const pasteSeq = useRef(0);

  // The underline: the whole document's text, re-derived as it changes.
  useEffect(() => {
    if (!editor) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const run = async () => {
      if (editor.isDestroyed) return;
      const found = await classifier.wontCompileIn(editor.state.doc.textContent);
      if (found && found.length > 0 && !editor.isDestroyed) {
        editor.commands.markWontCompile(found.map((w) => w.char));
      }
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(run, DOC_CHECK_DELAY_MS);
    };
    void run();
    editor.on("update", schedule);
    return () => {
      clearTimeout(timer);
      editor.off("update", schedule);
    };
  }, [editor, classifier]);

  /** Call with the pasted plain text. Never blocks or alters the paste itself. */
  const onPaste = useCallback(
    async (pasted: string) => {
      const seq = ++pasteSeq.current;
      const found = await classifier.wontCompileIn(pasted);
      // A newer paste replaces this one's chip, it doesn't queue behind it.
      if (seq !== pasteSeq.current || !found) return;
      if (found.length > 0) editor?.commands.markWontCompile(found.map((w) => w.char));
      setChip(chipFor(pasted, { checked: true, wontCompile: found }));
    },
    [classifier, editor],
  );

  const dismiss = useCallback(() => setChip(null), []);

  return { chip, onPaste, dismiss };
}
